import React, { useEffect, useState, useRef } from 'react';
import { Search, UserPlus, MoreVertical, X, Loader2, Edit, Trash, Copy, Check, AlertTriangle, ChevronDown } from 'lucide-react';
import { User, UserRole, UserStatus } from '../types';
import api from '../services/api';
import { ToastContainer, ToastProps } from '../components/ui/Toast';

// Simple Modal Component
const Modal = ({ isOpen, onClose, title, children }: { isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-800">
          <h3 className="text-xl font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, isLoading }: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  isLoading?: boolean;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <p className="text-sm text-slate-400 mt-1">{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            Eliminar Usuario
          </button>
        </div>
      </div>
    </div>
  );
};

// Credentials Modal
const CredentialsModal = ({ isOpen, onClose, email, password }: { isOpen: boolean; onClose: () => void; email: string; password: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(`Email: ${email}\nPassword: ${password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Credenciales de Usuario">
      <div className="space-y-4">
        <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-lg">
          <p className="text-sm text-blue-300">
            El usuario ha sido creado exitosamente. <br />
            Como el servicio de correo no está configurado, por favor copia y envía estas credenciales manualmente.
          </p>
        </div>

        <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 space-y-3">
          <div>
            <span className="text-xs text-slate-500 uppercase font-bold">Email</span>
            <p className="text-white font-mono">{email}</p>
          </div>
          <div>
            <span className="text-xs text-slate-500 uppercase font-bold">Contraseña Temporal</span>
            <p className="text-white font-mono text-lg tracking-wide">{password}</p>
          </div>
        </div>

        <button
          onClick={handleCopy}
          className="w-full py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-white transition-colors flex items-center justify-center gap-2"
        >
          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copiado al portapapeles' : 'Copiar Credenciales'}
        </button>

        <button
          onClick={onClose}
          className="w-full py-2 bg-primary hover:bg-blue-600 text-white rounded-lg transition-colors font-medium"
        >
          Entendido, cerrar
        </button>
      </div>
    </Modal>
  );
};

// MultiSelect Component
const MultiSelect = ({
  options,
  selectedIds,
  onChange
}: {
  options: { id: string; name: string }[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(selected => selected !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const selectedLabels = options
    .filter(opt => selectedIds.includes(opt.id))
    .map(opt => opt.name);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white flex items-center justify-between hover:border-slate-600 transition-colors focus:ring-2 focus:ring-primary focus:outline-none"
      >
        <div className="flex-1 truncate text-left mr-2">
          {selectedLabels.length > 0 ? (
            <span className="text-slate-200">{selectedLabels.join(', ')}</span>
          ) : (
            <span className="text-slate-500 selection:bg-transparent">Seleccionar proyectos...</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto">
          {options.length === 0 ? (
            <div className="p-3 text-sm text-slate-500 text-center">No hay proyectos disponibles</div>
          ) : (
            options.map(option => (
              <div
                key={option.id}
                onClick={() => toggleOption(option.id)}
                className="flex items-center gap-3 px-3 py-2 hover:bg-slate-700/50 cursor-pointer transition-colors"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedIds.includes(option.id) ? 'bg-primary border-primary' : 'border-slate-600 bg-slate-700'}`}>
                  {selectedIds.includes(option.id) && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-slate-200">{option.name}</span>
              </div>
            ))
          )}
        </div>
      )}
      <p className="text-xs text-slate-500 mt-1">
        {selectedIds.length} proyecto(s) asignado(s)
      </p>
    </div>
  );
};

interface ProjectOption {
  id: string;
  name: string;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Projects for assignment
  const [availableProjects, setAvailableProjects] = useState<ProjectOption[]>([]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Delete Confirmation State
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Credentials Display State
  const [showCredentials, setShowCredentials] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState({ email: '', password: '' });

  // Action Menu State
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Toasts
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const addToast = (type: 'success' | 'error' | 'info', message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, type, message, onDismiss: removeToast }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: UserRole.VIEWER,
    status: UserStatus.ACTIVE,
    project_ids: [] as string[]
  });

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser?.role === UserRole.ADMIN;

  useEffect(() => {
    loadUsers();
    loadProjects();
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadUsers();
  }, [roleFilter]);

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setOpenMenuId(null);
    }
  };

  const loadProjects = async () => {
    try {
      const projects = await api.getProjects();
      setAvailableProjects(projects.map((p: any) => ({ id: p.id, name: p.name })));
    } catch (error) {
      console.error("Failed to load projects", error);
    }
  }

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await api.getUsers({
        search: searchTerm || undefined,
        role: roleFilter || undefined,
        page_size: 50 // Fetch more to avoid pagination issues for now
      });
      setUsers(data.items);
      setStats({ total: data.total, active: data.active, pending: data.pending });
    } catch (error) {
      console.error('Failed to load users:', error);
      addToast('error', 'Error cargando usuarios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadUsers();
  };

  const handleOpenModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        status: user.status,
        project_ids: user.project_ids ? [...user.project_ids] : []
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: UserRole.VIEWER,
        status: UserStatus.ACTIVE,
        project_ids: []
      });
    }
    setIsModalOpen(true);
    setOpenMenuId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (editingUser) {
        const payload: any = {
          name: formData.name,
          role: formData.role,
          status: formData.status,
          project_ids: formData.project_ids
        };
        if (formData.password) payload.reset_password = formData.password;

        await api.updateUser(editingUser.id, payload);

        // Update local state partially for immediate feedback
        setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, project_ids: formData.project_ids } : u));

        addToast('success', 'Usuario actualizado correctamente');
        setIsModalOpen(false);
      } else {
        const tempPassword = formData.password || 'Temporary123!';
        await api.createUser({
          name: formData.name,
          email: formData.email,
          password: tempPassword,
          role: formData.role,
          project_ids: formData.project_ids
        });

        // Success flow for new user
        setIsModalOpen(false);
        setCreatedCredentials({ email: formData.email, password: tempPassword });
        setShowCredentials(true);
        addToast('success', 'Usuario creado exitosamente');
      }
      // Force reload to ensure consistency
      setTimeout(() => loadUsers(), 300);
    } catch (error: any) {
      console.error(error);
      addToast('error', error.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setIsSaving(false);
    }
  };

  const requestDelete = (user: User) => {
    setUserToDelete(user);
    setOpenMenuId(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await api.deleteUser(userToDelete.id);
      loadUsers();
      addToast('success', 'Usuario eliminado correctamente');
      setUserToDelete(null);
    } catch (error: any) {
      console.error(error);
      addToast('error', error.response?.data?.detail || 'Error eliminando usuario');
    } finally {
      setIsDeleting(false);
    }
  }

  // ... (keep helper functions like getRoleBadge, etc. same as before) ...
  const getRoleBadge = (role: UserRole) => {
    const styles: Record<UserRole, string> = {
      [UserRole.GLOBAL_ADMIN]: 'bg-indigo-500/10 text-indigo-400',
      [UserRole.TENANT_ADMIN]: 'bg-rose-500/10 text-rose-400',
      [UserRole.ADMIN]: 'bg-purple-500/10 text-purple-400',
      [UserRole.QA_LEAD]: 'bg-blue-500/10 text-blue-400',
      [UserRole.QA_TESTER]: 'bg-green-500/10 text-green-400',
      [UserRole.QA_AUTOMATION]: 'bg-cyan-500/10 text-cyan-400',
      [UserRole.VIEWER]: 'bg-slate-500/10 text-slate-400',
    };
    return styles[role] || styles[UserRole.VIEWER];
  };

  const getStatusBadge = (status: UserStatus) => {
    const styles: Record<UserStatus, string> = {
      [UserStatus.ACTIVE]: 'bg-green-500/10 text-green-400',
      [UserStatus.INACTIVE]: 'bg-slate-500/10 text-slate-400',
      [UserStatus.SUSPENDED]: 'bg-red-500/10 text-red-400',
      [UserStatus.PENDING]: 'bg-yellow-500/10 text-yellow-400',
    };
    return styles[status] || styles[UserStatus.PENDING];
  };

  const formatRole = (role: UserRole) => {
    const labels: Record<UserRole, string> = {
      [UserRole.GLOBAL_ADMIN]: 'Global Admin',
      [UserRole.TENANT_ADMIN]: 'Tenant Admin',
      [UserRole.ADMIN]: 'Administrador',
      [UserRole.QA_LEAD]: 'QA Lead',
      [UserRole.QA_TESTER]: 'QA Tester',
      [UserRole.QA_AUTOMATION]: 'QA Automation',
      [UserRole.VIEWER]: 'Viewer',
    };
    return labels[role] || role;
  };

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
              <span className="text-blue-400 text-lg">👥</span>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Total Usuarios</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
              <span className="text-green-400 text-lg">✓</span>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Usuarios Activos</p>
              <p className="text-2xl font-bold text-white">{stats.active}</p>
            </div>
          </div>
        </div>
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/10 rounded-full flex items-center justify-center">
              <span className="text-yellow-400 text-lg">⏳</span>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Pendientes Onboarding</p>
              <p className="text-2xl font-bold text-white">{stats.pending}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre, email o cargo..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-primary"
          />
        </form>

        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white"
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="qa_lead">QA Lead</option>
          <option value="qa_tester">QA Tester</option>
          <option value="qa_automation">QA Automation</option>
          <option value="viewer">Viewer</option>
        </select>

        {isAdmin && (
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Invitar Usuario
          </button>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-surface-dark border border-slate-800 rounded-xl overflow-visible min-h-[400px]">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-800/50">
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Usuario</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Proyectos</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Rol de Acceso</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Estado</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Última Conexión</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-slate-400">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center">
                        <span className="text-sm font-semibold text-white">
                          {user.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.project_ids && user.project_ids.length > 0 ? (
                        <span className="text-sm text-slate-300">
                          {user.project_ids.map(id => availableProjects.find(p => p.id === id)?.name).filter(Boolean).join(', ')}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500 italic">Sin asignación</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                      {formatRole(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-400">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString('es-CL') : 'Never'}
                  </td>
                  <td className="px-6 py-4 relative">
                    {isAdmin && (
                      <div className="relative">
                        <button
                          onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)}
                          className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-4 h-4 text-slate-400" />
                        </button>
                        {openMenuId === user.id && (
                          <div ref={menuRef} className="absolute right-0 top-full mt-1 w-48 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-20 py-1">
                            <button
                              onClick={() => handleOpenModal(user)}
                              className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 flex items-center gap-2"
                            >
                              <Edit className="w-4 h-4" /> Editar
                            </button>
                            <button
                              onClick={() => requestDelete(user)}
                              className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-800 flex items-center gap-2"
                            >
                              <Trash className="w-4 h-4" /> Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Invite/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingUser ? 'Editar Usuario' : 'Invitar Nuevo Usuario'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Nombre Completo</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Email Corporativo</label>
            <input
              type="email"
              required
              disabled={!!editingUser}
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white disabled:opacity-50"
            />
          </div>
          {!editingUser && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Contraseña Inicial</label>
              <div className="relative">
                <input
                  type="text"
                  required
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  minLength={8}
                  maxLength={72}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="Generar contraseña segura (8-72 caracteres)..."
                />
                <button
                  type="button"
                  onClick={() => {
                    // Generate 12-character password: 8 random + 'Aa1!' = 12 total (within 72 char limit)
                    const randomPart = Math.random().toString(36).substring(2, 10); // 8 chars
                    setFormData({ ...formData, password: randomPart + 'Aa1!' });
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary hover:underline"
                >
                  Generar
                </button>
              </div>
            </div>
          )}
          {editingUser && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Resetear Contraseña (Opcional)</label>
              <input
                type="text"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                minLength={8}
                maxLength={72}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                placeholder="Dejar vacío para mantener actual (8-72 caracteres)"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Rol</label>
              <select
                value={formData.role}
                onChange={e => setFormData({ ...formData, role: e.target.value as UserRole })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                <option value={UserRole.ADMIN}>Admin</option>
                <option value={UserRole.QA_LEAD}>QA Lead</option>
                <option value={UserRole.QA_TESTER}>QA Tester</option>
                <option value={UserRole.QA_AUTOMATION}>QA Automation</option>
                <option value={UserRole.VIEWER}>Viewer</option>
              </select>
            </div>
            {editingUser && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Estado</label>
                <select
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value as UserStatus })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                >
                  <option value={UserStatus.ACTIVE}>Activo</option>
                  <option value={UserStatus.PENDING}>Pendiente</option>
                  <option value={UserStatus.SUSPENDED}>Suspendido</option>
                  <option value={UserStatus.INACTIVE}>Inactivo</option>
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Asignar Proyectos</label>
            <MultiSelect
              options={availableProjects}
              selectedIds={formData.project_ids}
              onChange={(ids) => setFormData({ ...formData, project_ids: ids })}
            />
            <p className="text-xs text-slate-500 mt-1">El usuario solo podrá ver los proyectos seleccionados (Admin ve todo).</p>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-primary hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingUser ? 'Guardar Cambios' : 'Enviar Invitación'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="¿Eliminar Usuario?"
        message={`Esta acción eliminará al usuario "${userToDelete?.name}" permanentemente. No se puede deshacer.`}
        isLoading={isDeleting}
      />

      {/* Credentials Modal (Feedback for new users) */}
      <CredentialsModal
        isOpen={showCredentials}
        onClose={() => setShowCredentials(false)}
        email={createdCredentials.email}
        password={createdCredentials.password}
      />
    </div>
  );
};

export default Users;
